import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, EMPTY, merge, Observable, shareReplay, Subject, switchMap, tap, timer } from 'rxjs';
import { environment } from 'src/environments/environment';
import { ISupervisorShift, ITeamSummary } from './check-in.service';

// Attendance Tracking (Performance): team-scoped days worked / days off from check-ins + approved
// SIC/VAC leave, with supervisor overrides and employee correction requests.

// AB#1096: one day of an hourly employee's shift clock (UTC instants, shown in timeZoneId).
export interface IAttendanceClockDay {
    clockInAt: string;
    clockOutAt: string | null;
    mealStartAt: string | null;
    mealEndAt: string | null;
    hours: number;                  // net of lunch
    mealMinutes: number;
    shiftCount: number;
    timeZoneId: string;
    zoneAbbreviation: string;
    stillClockedIn: boolean;
    missingClockOut: boolean;
    missingMeal: boolean;           // a completed shift over 5 hours with no lunch
    edited: boolean;
    lastEditNotes?: string | null;
    lastEditedByName?: string | null;
}

export interface IAttendanceDay {
    date: string;
    status: string;                 // Present | Remote | Partial | Vacation | Sick | Holiday | Excused | Absent | Off
    source: string;                 // override | leave | clock | checkin | schedule
    hours?: number | null;
    note?: string | null;
    isOverride: boolean;
    setByName?: string | null;
    scheduled: boolean;
    clock?: IAttendanceClockDay | null;      // shift clock detail (hourly employees)
    checkInAt?: string | null;               // first one-tap check-in that day (salaried)
    checkInTimeZoneId?: string | null;
}

export interface IAttendanceSummary {
    daysWorked: number;
    daysOff: number;
    vacation: number;
    sick: number;
    holiday: number;
    excused: number;
    absent: number;
    totalHours: number;
    lunchMinutes?: number;          // shift-clock lunch this week (hourly)
}

export interface IAttendanceMember {
    userId: number;
    name: string;
    isSupervisor: boolean;
    isHourly?: boolean;             // hourly (shift clock) rows show clock times; salaried rows show Present
    days: IAttendanceDay[];
    summary: IAttendanceSummary;
}

export interface IAttendanceWeek {
    teamId: number;
    teamName: string;
    weekStart: string;
    workDaysMask: number;
    days: string[];
    canManage: boolean;
    members: IAttendanceMember[];
}

export interface IAttendanceChangeRequest {
    id: number;
    userId: number;
    employeeName: string;
    date: string;
    requestedStatus: string;
    reason: string;
    status: string;                 // Pending | Approved | Denied | Cancelled
    submittedByName: string;
    submittedOn: string;
    decidedByName?: string | null;
    decidedOn?: string | null;
    decisionNote?: string | null;
    canDecide: boolean;             // AB#1119: manager escalation visibility never grants approval rights.
}

export interface IAttendanceRequestTeam {
    id: number;
    name: string;
    canDecide: boolean;
    pendingCount: number;
}

export interface IAttendancePendingCounts {
    total: number;
    teams: { teamId: number; pendingCount: number }[];
}

export interface IAttendanceSchedule {
    teamId: number;
    workDaysMask: number;
    canManage: boolean;
}

export interface IAttendanceOverride {
    userId: number;
    date: string;
    status: string;
    note?: string | null;
    hours?: number | null;
}

export interface IAttendanceChangeSubmit {
    date: string;
    requestedStatus: string;
    reason: string;
}

// AB#1096: an hourly employee's own shift-clock week, and their requests to correct it.
export interface IMyClockWeek {
    eligible: boolean;
    timeZoneId: string;             // the zone a missing shift is entered in
    shifts: ISupervisorShift[];     // canEdit = may request a correction (false only when payroll-locked)
}

export interface IClockEditRequest {
    id: number;
    userId: number;
    employeeName: string;
    shiftId: number | null;
    requestType: string;            // Correct | Add
    workDate: string;               // yyyy-MM-dd
    timeZoneId: string;
    currentClockInAt: string | null;
    currentClockOutAt: string | null;
    currentMealStartAt: string | null;
    currentMealEndAt: string | null;
    requestedClockInAt: string;
    requestedClockOutAt: string | null;
    requestedMealStartAt: string | null;
    requestedMealEndAt: string | null;
    reason: string;
    status: string;                 // Pending | Approved | Denied | Cancelled
    submittedOn: string;
    decidedByName?: string | null;
    decidedOn?: string | null;
    decisionNote?: string | null;
}

export interface IClockEditSubmit {
    shiftId: number | null;
    clockInAt: string;
    clockOutAt: string | null;
    mealStartAt: string | null;
    mealEndAt: string | null;
    timeZoneId: string | null;
    reason: string;
}

export interface IClockEditQueue {
    canApprove: boolean;
    items: IClockEditRequest[];
}

@Injectable({ providedIn: 'root' })
export class AttendanceService {

    private base = `${environment.adminApiUrl}/attendance`;
    private pendingRefresh = new Subject<void>();

    /** AB#1119: one shared poll for the sidebar and queue; recover inside each fetch after an outage. */
    public readonly pendingCounts$ = merge(timer(0, 60000), this.pendingRefresh).pipe(
        switchMap(() => this.getPendingCounts().pipe(catchError(() => EMPTY))),
        shareReplay({ bufferSize: 1, refCount: true })
    );

    constructor(private httpClient: HttpClient) { }

    public getMyTeams(): Observable<ITeamSummary[]> {
        return this.httpClient.get<ITeamSummary[]>(`${this.base}/my-teams`);
    }

    public getStatuses(): Observable<string[]> {
        return this.httpClient.get<string[]>(`${this.base}/statuses`);
    }

    /** weekStart = 'yyyy-MM-dd' (any date in the week; the server snaps it to the Sunday). */
    public getTeamWeek(teamId: number, weekStart: string): Observable<IAttendanceWeek> {
        const params = new HttpParams().set('weekStart', weekStart);
        return this.httpClient.get<IAttendanceWeek>(`${this.base}/team/${teamId}`, { params });
    }

    public getMyWeek(weekStart: string): Observable<IAttendanceWeek> {
        const params = new HttpParams().set('weekStart', weekStart);
        return this.httpClient.get<IAttendanceWeek>(`${this.base}/my`, { params });
    }

    public setOverride(req: IAttendanceOverride): Observable<void> {
        return this.httpClient.put<void>(`${this.base}/override`, req);
    }

    public submitChangeRequest(req: IAttendanceChangeSubmit): Observable<IAttendanceChangeRequest> {
        return this.httpClient.post<IAttendanceChangeRequest>(`${this.base}/change-request`, req)
            .pipe(tap(() => this.pendingRefresh.next()));
    }

    public myChangeRequests(): Observable<IAttendanceChangeRequest[]> {
        return this.httpClient.get<IAttendanceChangeRequest[]>(`${this.base}/change-requests/mine`);
    }

    public getChangeRequests(teamId: number, status?: string | null): Observable<IAttendanceChangeRequest[]> {
        let params = new HttpParams().set('teamId', teamId);
        if (status) { params = params.set('status', status); }
        return this.httpClient.get<IAttendanceChangeRequest[]>(`${this.base}/change-requests`, { params });
    }

    public getRequestTeams(): Observable<IAttendanceRequestTeam[]> {
        return this.httpClient.get<IAttendanceRequestTeam[]>(`${this.base}/change-requests/teams`);
    }

    public getPendingCounts(): Observable<IAttendancePendingCounts> {
        return this.httpClient.get<IAttendancePendingCounts>(`${this.base}/change-requests/pending-counts`);
    }

    public approveChangeRequest(id: number, note?: string | null): Observable<IAttendanceChangeRequest> {
        return this.httpClient.put<IAttendanceChangeRequest>(`${this.base}/change-requests/${id}/approve`, { note: note ?? null })
            .pipe(tap(() => this.pendingRefresh.next()));
    }

    public denyChangeRequest(id: number, note: string): Observable<IAttendanceChangeRequest> {
        return this.httpClient.put<IAttendanceChangeRequest>(`${this.base}/change-requests/${id}/deny`, { note })
            .pipe(tap(() => this.pendingRefresh.next()));
    }

    public getSchedule(teamId: number): Observable<IAttendanceSchedule> {
        return this.httpClient.get<IAttendanceSchedule>(`${this.base}/schedule/${teamId}`);
    }

    public setSchedule(teamId: number, workDaysMask: number): Observable<void> {
        return this.httpClient.put<void>(`${this.base}/schedule/${teamId}`, { workDaysMask });
    }

    // ---------- shift-clock requests (AB#1096) ----------

    public getMyClockWeek(weekStart: string): Observable<IMyClockWeek> {
        const params = new HttpParams().set('weekStart', weekStart);
        return this.httpClient.get<IMyClockWeek>(`${this.base}/clock/my-week`, { params });
    }

    public submitClockRequest(req: IClockEditSubmit): Observable<IClockEditRequest> {
        return this.httpClient.post<IClockEditRequest>(`${this.base}/clock/requests`, req);
    }

    public myClockRequests(): Observable<IClockEditRequest[]> {
        return this.httpClient.get<IClockEditRequest[]>(`${this.base}/clock/requests/mine`);
    }

    public cancelClockRequest(id: number): Observable<IClockEditRequest> {
        return this.httpClient.put<IClockEditRequest>(`${this.base}/clock/requests/${id}/cancel`, {});
    }

    public getClockQueue(status?: string | null): Observable<IClockEditQueue> {
        let params = new HttpParams();
        if (status) { params = params.set('status', status); }
        return this.httpClient.get<IClockEditQueue>(`${this.base}/clock/requests/queue`, { params });
    }

    public approveClockRequest(id: number, note?: string | null): Observable<IClockEditRequest> {
        return this.httpClient.put<IClockEditRequest>(`${this.base}/clock/requests/${id}/approve`, { note: note ?? null });
    }

    public denyClockRequest(id: number, note: string): Observable<IClockEditRequest> {
        return this.httpClient.put<IClockEditRequest>(`${this.base}/clock/requests/${id}/deny`, { note });
    }
}
